import { Avatar, Card, Divider, Grid, Group, Stack, Text } from "@mantine/core"
import { Icon } from "@iconify/react"
import { createColumnHelper } from "@tanstack/react-table"
import React from "react"
import { SuperAdminTenantTabs } from "src/components/superadmin/tenants/SuperAdminTenantTabs"
import { CreateSuperAdminTenantUserAction, UpdateSuperAdminTenantUserAction } from "src/components/superadmin/tenants/users/SuperAdminTenantUserActions"
import { makeEnumBadgeComponent } from "src/components/ui/Badges"
import { DataTable } from "src/components/ui/DataTable"
import { LoadMoreButton } from "src/components/ui/LoadMoreButton"
import { EmptyPageComponent } from "src/components/ui/Misc"
import { ModuleErrors } from "src/components/ui/ModuleErrors"
import { PageFilters, usePageFilters } from "src/components/ui/PageFilters"
import { PageHeader } from "src/components/ui/PageHeader"
import { PageLayout } from "src/components/ui/PageLayout"
import { useActions, useAppState } from "src/state"
import { SetMetaInput } from "src/state/Module"
import { UserFragment, User_Roles_Enum, User_Status_Enum, User_Type_Enum, Users_Bool_Exp } from "src/state/gql/_types"
import { USER_STATUS_ENUM_PALETTE, USER_TYPE_ENUM_PALETTE } from "src/state/palettes"

const UserStatusBadge = makeEnumBadgeComponent(User_Status_Enum, 'filled', 'lg', USER_STATUS_ENUM_PALETTE)
const UserTypeBadge = makeEnumBadgeComponent(User_Type_Enum, 'outline', 'lg', USER_TYPE_ENUM_PALETTE)
const UserRoleBadge = makeEnumBadgeComponent(User_Roles_Enum, 'outline', 'lg', USER_TYPE_ENUM_PALETTE)

export function SuperAdminTenantUsers() {

    const { itemsList, errors, meta, loadingMore, loading, meta: { limit, offset, sortKey, sortOrder } } = useAppState(state => state.users)
    const { 
        subscribeToItems, 
        unsubscribeFromItems, 
        getMoreItems, 
        resetModule, 
        setMeta,
        selectItem
    } = useActions().users
    const { view } = useAppState(state => state.ui)
    const { currentItemId: currentTenantId } = useAppState(state=>state.tenants)

    const {
        filters,
        filterParams
    } = usePageFilters<Users_Bool_Exp['_and'], UserFragment>({
        baseFilters: [
            { tenant_id: { _eq: currentTenantId } }
        ],
        clickFilters: [
            {
                _or: [
                    { status: { _eq: User_Status_Enum.Active } }
                ]
            }
        ],
        searchFilterFields: [],
        searchFilterRelations: {},
        clickFilterField: 'status',
        clickFiltersDisableDefaultAll: true,
        clickFilterDefault: 'active',
        clickFilterValues: {
            active: { label: 'Active', filter: [{ status: { _eq: User_Status_Enum.Active } }] },
            inactive: { label: 'Inactive', filter: [{ status: { _eq: User_Status_Enum.Inactive } }] },
            all: { label: 'All', filter: [] }
        }
    })

    React.useEffect(() => {
        subscribeToItems(filters);
        return () => {
            unsubscribeFromItems()
        }
    }, [filters, limit, offset, sortKey, sortOrder, subscribeToItems, unsubscribeFromItems, resetModule])

    const columnHelper = createColumnHelper<UserFragment>()

    const columns = React.useMemo(() => [
        columnHelper.accessor('id', {
            header: 'Id'
        }),
        columnHelper.accessor('user_number', {
            header: '#'
        }),
        columnHelper.accessor(row => row.user_status, {
            id: 'status',
            meta: {
                relationKey: 'user_status',
                sortKey: 'value'
            },
            header: 'Status',
            cell: row => <UserStatusBadge status={row.getValue()!} />
        }),
        columnHelper.accessor(row => row.user_type, {
            id: 'type',
            meta: {
                relationKey: 'user_type',
                sortKey: 'value'
            },
            header: 'Type',
            cell: row => <UserTypeBadge status={row.getValue()!} />
        }),
        columnHelper.accessor(row => row.user_role, {
            id: 'role',
            meta: {
                relationKey: 'user_role',
                sortKey: 'value'
            },
            header: 'Role',
            cell: row => <UserRoleBadge status={row.getValue()!} />
        }),
        columnHelper.accessor('first_name', {
            header: 'First Name'
        }),
        columnHelper.accessor('last_name', {
            header: 'Last Name'
        }),
        columnHelper.accessor('city', {
            header: 'City'
        }),
        columnHelper.accessor('state', {
            header: 'State'
        }),
        columnHelper.accessor('phone', {
            header: 'Phone'
        }),
    ], [columnHelper])

    const initialState = {
        columnVisibility: {
            id: false
        }
    }

    const handleMetaChange = (meta: SetMetaInput<UserFragment>) => {
        setMeta(meta)
    }

    const handleSelect = (id: string) => {
        selectItem(id)
    }

    const renderCards = () => {
        return (
            <React.Fragment>
                <Grid>
                    {itemsList.map((i, k) => {
                        const initials = [i.first_name, i.last_name]
                            .filter(Boolean)
                            .map(n => n!.charAt(0).toUpperCase())
                            .join('')
                        const fullName = [i.first_name, i.last_name].filter(Boolean).join(' ') || i.name || '—'
                        const location = [i.city, i.state].filter(Boolean).join(', ')
                        const displayName = i.alias || i.dj_display
                        const hasSocial = i.instagram || i.website || i.facebook || i.x || i.tiktok
                        return (
                            <Grid.Col span={{ base: 12, sm: 6, lg: 4 }} key={k}>
                                <Card
                                    onClick={() => handleSelect(i.id)}
                                    shadow="sm"
                                    padding="lg"
                                    radius="md"
                                    className="card-theme-global"
                                    h="100%"
                                    style={{ cursor: 'pointer' }}
                                >
                                    <Group justify="space-between" align="flex-start" wrap="nowrap" mb="xs">
                                        <Group gap="md" wrap="nowrap" style={{ minWidth: 0 }}>
                                            <Avatar
                                                size="lg"
                                                radius="md"
                                                color={i.color || 'blue.5'}
                                                style={{ flexShrink: 0 }}
                                            >
                                                {initials || '?'}
                                            </Avatar>
                                            <Stack gap={2} style={{ minWidth: 0 }}>
                                                <Text fw={700} size="md" lineClamp={1}>{fullName}</Text>
                                                {displayName && <Text size="xs" c="dimmed" fs="italic" lineClamp={1}>{displayName}</Text>}
                                                <Text size="xs" c="dimmed">#{i.user_number}</Text>
                                            </Stack>
                                        </Group>
                                        {i.user_status && <UserStatusBadge status={i.user_status} />}
                                    </Group>

                                    <Group gap="xs" mb="md">
                                        {i.user_type && <UserTypeBadge status={i.user_type} />}
                                        {i.user_role && <UserRoleBadge status={i.user_role} />}
                                    </Group>

                                    <Divider mb="sm" />

                                    <Stack gap="xs">
                                        {i.email && (
                                            <Group gap="xs" wrap="nowrap">
                                                <Icon icon="ph:envelope-duotone" width={15} style={{ flexShrink: 0, opacity: 0.6 }} />
                                                <Text size="sm" c="dimmed" lineClamp={1}>{i.email}</Text>
                                            </Group>
                                        )}
                                        {location && (
                                            <Group gap="xs" wrap="nowrap">
                                                <Icon icon="ph:map-pin-duotone" width={15} style={{ flexShrink: 0, opacity: 0.6 }} />
                                                <Text size="sm" c="dimmed" lineClamp={1}>{location}</Text>
                                            </Group>
                                        )}
                                        {i.phone && (
                                            <Group gap="xs" wrap="nowrap">
                                                <Icon icon="ph:phone-duotone" width={15} style={{ flexShrink: 0, opacity: 0.6 }} />
                                                <Text size="sm" c="dimmed">{i.phone}</Text>
                                            </Group>
                                        )}
                                    </Stack>

                                    {hasSocial && (
                                        <>
                                            <Divider mt="sm" mb="xs" />
                                            <Group gap="sm">
                                                {i.instagram && <Icon icon="ph:instagram-logo-duotone" width={16} style={{ opacity: 0.6 }} />}
                                                {i.website && <Icon icon="ph:globe-duotone" width={16} style={{ opacity: 0.6 }} />}
                                                {i.facebook && <Icon icon="ph:facebook-logo-duotone" width={16} style={{ opacity: 0.6 }} />}
                                                {i.x && <Icon icon="ph:x-logo-duotone" width={16} style={{ opacity: 0.6 }} />}
                                                {i.tiktok && <Icon icon="ph:tiktok-logo-duotone" width={16} style={{ opacity: 0.6 }} />}
                                            </Group>
                                        </>
                                    )}
                                </Card>
                            </Grid.Col>
                        )
                    })}
                </Grid>
                <LoadMoreButton meta={meta} onLoadMore={getMoreItems} loading={loadingMore} />
            </React.Fragment>
        )
    }

    const renderList = () => {
        return (
            <DataTable<UserFragment>
                columns={columns}
                data={itemsList}
                loading={loading}
                meta={meta}
                onMetaChange={handleMetaChange}
                initialState={initialState}
                onSelectItem={handleSelect}
            />
        )
    }

    return (
        <PageLayout
            header={
                <PageHeader
                    title={`Users`}
                    renderAction={
                        <Group>
                            <UpdateSuperAdminTenantUserAction />
                            <CreateSuperAdminTenantUserAction/>
                        </Group>
                    }
                />
            }
        >
            <SuperAdminTenantTabs/>
            <PageFilters options={filterParams} />
            <ModuleErrors errors={errors} />
            {itemsList.length ? (<> {view === 'list' ? renderList() : renderCards()} </>) : (<EmptyPageComponent label='Users' />)}
        </PageLayout>
    )

}